import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SxProps,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import AutocompleteChip from "../../customAutocomplete/AutocompleteChip";

export interface SelectOption {
  value: string;
  label: string;
}

type Props<T = SelectOption> = {
  value: string | string[];
  onChange: (event: SelectChangeEvent<string | string[]>) => void;
  options: T[];
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  multiple?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputSx?: SxProps;
  error?: boolean;
  showChips?: boolean;
  label?: string;
  showCheckboxes?: boolean;
  readonly?: boolean;
  chipVariant?: "filled" | "outlined";
  name?: string;
};

const CommonSelectField = <T,>({
  value,
  onChange,
  options,
  getOptionLabel,
  getOptionValue,
  multiple = false,
  fullWidth = true,
  disabled = false,
  placeholder,
  inputSx = {},
  error = false,
  showChips = false,
  label,
  showCheckboxes = false,
  readonly = false,
  chipVariant = "filled",
  name,
}: Props<T>) => {
  // If multiple is enabled, then value is string array, otherwise string
  const currentValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value || "";

  const resolveOptionValue = (option: T) =>
    getOptionValue ? getOptionValue(option) : (option as SelectOption).value;

  const resolveOptionLabel = (option: T) =>
    getOptionLabel ? getOptionLabel(option) : (option as SelectOption).label;

  const handleDeleteChip = (valToRemove: string) => {
    const next = Array.isArray(currentValue)
      ? currentValue.filter((v) => v !== valToRemove)
      : [];
    const syntheticEvent = {
      target: { value: next, name: name || "" },
    } as unknown as SelectChangeEvent<string | string[]>;
    onChange(syntheticEvent);
  };

  // Custom rendering for select value: as chips or comma-separated list
  const renderValue = (selected: any) => {
    if (multiple && showChips) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selected.map((val: string) => (
            <AutocompleteChip
              variant={chipVariant}
              key={val}
              label={
                options.find((option) => resolveOptionValue(option) === val)
                  ? resolveOptionLabel(
                      options.find(
                        (option) => resolveOptionValue(option) === val,
                      )!,
                    )
                  : val
              }
              disabled={disabled}
              onDelete={() => handleDeleteChip(val)}
              readOnly={readonly}
              alwaysStopPropagation
            />
          ))}
        </Box>
      );
    }
    if (multiple && !showChips) {
      return selected
        .map((val: string) =>
          options.find((option) => resolveOptionValue(option) === val)
            ? resolveOptionLabel(
                options.find((option) => resolveOptionValue(option) === val)!,
              )
            : val,
        )
        .join(", ");
    }
    // For single value
    if (selected === "") {
      return "";
    }
    const sel = options.find(
      (option) => resolveOptionValue(option) === selected,
    );
    return sel ? resolveOptionLabel(sel) : placeholder || "";
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} sx={inputSx}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        // TODO: missing required attribute/handling
        multiple={multiple}
        value={currentValue}
        onChange={onChange}
        displayEmpty
        disabled={disabled}
        renderValue={renderValue}
        size="small"
        label={label}
        name={name}
      >
        {options.map((option) => {
          const optionValue = resolveOptionValue(option);
          const optionLabel = resolveOptionLabel(option);
          return (
            <MenuItem key={optionValue} value={optionValue}>
              {multiple && showCheckboxes && (
                <Checkbox
                  checked={
                    Array.isArray(currentValue)
                      ? currentValue.includes(optionValue)
                      : false
                  }
                />
              )}
              <ListItemText primary={optionLabel} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default CommonSelectField;
